import { LocaleData } from '../../../models/locale/locale-data';
import { LandingVariantCopy } from '../../../models/landing/variant';

// New-UX (`-v1`) shared copy — the approved redesign strings (mockup pack
// round 3, 26-Aug-2026 product feedback). The three `-v1` variant keys reuse
// this block; ` **` footnote markers on the CTA headings are appended by the
// components, not stored here.
const v2Copy: LandingVariantCopy = {
  banner: 'REGÍSTRESE AHORA',
  heroCopy: '¡Buena elección con su compra!',
  heroSubCopy1:
    'Registre ahora su electrodoméstico en un plazo de 60 días para desbloquear sus beneficios gratuitos',
  benefitTicks: [
    'Garantía gratuita de 10 años en repuestos*',
    'Piezas originales de Candy',
    'Protección adicional opcional frente a daños accidentales y averías',
  ],
  heroDisclaimer:
    'La garantía cubre piezas como motores, bombas, placas de control y otros componentes elegibles',
  registration: 'Llame ahora para activar',
  ctaPhoneText: 'Llamar al 917 885 132',
  ctaPhoneLink: 'tel:+34917885132',
  phoneSubCopy: 'Líneas abiertas de lunes a viernes de 9:00 a 18:00.',
  onlineCopy: 'Regístrese online en pocos minutos',
  ctaOnlineText: 'Regístrese online',
  sixtyDayHeader: 'Regístrese en un plazo de 60 días',
  sixtyDayBody:
    'Active su garantía gratuita de 10 años en repuestos antes de que finalice el plazo de registro',
};

// LP1 hybrid (`phone-only-v2`, Sept 2026) — LP COPY V2_SEPT 2026 sheets as
// applied in the approved mockups (claude-notes/lp-hybrid-lp1). Dials the
// same number as V1: `ctaPhoneLink` is V1's (undefined = the base link).
// The `**` on the first heading is appended by PhoneOnlyV2.
const lp1HybridCopy: LandingVariantCopy = {
  banner: 'Regístrese ahora',
  offerHeadline: ['10 AÑOS', 'de garantía en repuestos*'],
  sixtyDayHeader:
    'Registro necesario dentro de los primeros 60 días de la compra del electrodoméstico',
  registration: 'Llame ahora para activar',
  ctaPhoneText: 'Llame al 917 697 977',
  // Own dial link (not V1's): V1 Spain may use the control number on prod (PR #42/#43,
  // carrier outage 29 Sep); the hybrid keeps its own number.
  ctaPhoneLink: 'tel:+34917697977',
  benefitsHeader: 'Disfrute ahora sus beneficios gratuitos:',
  benefitTicks: [
    'Garantía gratuita de 10 años en repuestos',
    'Piezas originales de Candy',
    'Protección adicional opcional frente a daños accidentales y averías',
  ],
  phoneSubCopy: 'El horario de atención es de lunes a viernes de 9:00 a 18:00.',
  repeatHeading: '¿Listo para registrarse?',
  ctaPhoneRepeatText: 'Llame ahora al 917 697 977',
};

export const data: LocaleData = {
  banner: 'Registre GRATIS',
  heroNumber: '10',
  heroCopy: 'años de garantía en repuestos*',
  heroSubCopy1: '<b>Registro necesario</b>',
  heroSubCopy2: 'dentro de los primeros 60 días de la compra del electrodoméstico',
  registration: 'Llame ahora y active**',
  ctaPhoneText: '917 885 132',
  ctaPhoneLink: 'tel:917885132',
  phoneSubCopy:
    'El registro telefónico es rápido y le proporciona información adicional sobre la garantía del fabricante y la garantía de 10 años en repuestos. El horario de atención es de lunes a viernes de 9:00 a 18:00.',
  onlineCopy: 'También puede registrarse en línea.',
  ctaOnlineText: 'Haga click aquí.',
  ctaOnlineLink: 'https://candy.register-appliance.com/RegistroPorMarca?country=ES&language=es',
  onlineJourneyLaunch: 'Live',
  variants: {
    'phone-only-v1': v2Copy,
    'phone-and-link-v1': v2Copy,
    'phone-only-v2': lp1HybridCopy,
    'link-only-v1': {
      ...v2Copy,
      ctaOnlineText: 'Haga clic aquí para registrarse ahora',
      linesClosedMessage:
        'Nuestras líneas telefónicas están cerradas actualmente, pero puede registrarse online en cualquier momento',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG, sucursal en España, registrará su electrodoméstico. Además, durante el registro, si usted está interesado, Domestic & General le proporcionará información relativa a ofertas de seguro de daños para los electrodomésticos de su hogar. Para conocer cómo Candy Hoover Group S.r.l. y Domestic & General tratan sus datos visite <0>www.candy-home.com/es_ES/pages/politica-de-privacidad</0> y <1>www.domesticandgeneral.com/candy</1>.',
      ],
      phoneNumberAsterixLinks: [
        'https://www.candy-home.com/es_ES/pages/politica-de-privacidad',
        'https://www.domesticandgeneral.com/candy',
      ],
    },
    // LP3 COPY V1_JULY 2026.xlsx (CANDY tab). Banner/hero already match the base copy.
    'link-only': {
      registration:
        'Registre ahora su electrodoméstico para desbloquear sus beneficios gratuitos**',
      onlineCopy:
        'Nuestras líneas telefónicas están cerradas actualmente, pero puede registrarse online en cualquier momento',
      ctaOnlineText: 'Regístrese en línea',
      phoneNumberAsterix: [
        '** Domestic & General Insurance Europe AG, sucursal en España, registrará su electrodoméstico. Además, durante el registro, si usted está interesado, Domestic & General le proporcionará información relativa a ofertas de seguro de daños para los electrodomésticos de su hogar. Para conocer cómo Candy Hoover Group S.r.l. y Domestic & General tratan sus datos visite <0>www.candy-home.com/es_ES/pages/politica-de-privacidad</0> y <1>www.domesticandgeneral.com/candy</1>.',
      ],
      phoneNumberAsterixLinks: [
        'https://www.candy-home.com/es_ES/pages/politica-de-privacidad',
        'https://www.domesticandgeneral.com/candy',
      ],
    },
  },
  benefitsHeader: 'Sus beneficios al registrarse:',
  benefits: [
    '10 años de garantía gratuita en repuestos',
    'Acceso rápido a nuestra red de servicio oficial',
    'Información importante sobre su electrodoméstico y medidas de seguridad',
    'Descuentos y ofertas exclusivas sobre productos, servicios y asistencia adicional',
  ],
  footerLinks: [
    {
      text: 'Informaciones legales',
      link: '/terms',
    },
    { text: 'Política de Privacidad', link: 'https://www.domesticandgeneral.com/candy' },
    { text: 'Cookies', link: '/cookies' },
  ],
  footerCopy:
    'Este sitio web es propiedad de Domestic & General Group Limited y está operado por Domestic & General Insurance Europe AG, Hagenauer Straße 44, 65203 Wiesbaden, Germany. Domestic & General Insurance Europe AG está autorizada y regulada por Autoridad Federal de Supervisión Financiera (BaFin) en Alemania. ',
  heroAsterix: [
    '* Al registrar su aparato dentro de los 60 días a partir de la fecha de compra, activará 10 años de garantía GRATUITA en repuestos, incluidos los años ya cubiertos por la garantía legal. Mano de obra y otros gastos de reparación eventuales no incluidos. Términos y condiciones aplicables, disponibles en <0>www.candy-home.com/es_ES/pages/terminos-y-condiciones-extension-de-la-garantia</0>. Registro necesario. ',
  ],
  heroAsterixLinks: [
    'https://www.candy-home.com/es_ES/pages/terminos-y-condiciones-extension-de-la-garantia',
  ],
  phoneNumberAsterix: [
    '** Número de teléfono fijo operado por Domestic & General Insurance Europe AG, sucursal en España. Se aplica la tarifa de llamadas locales habitual de su proveedor de telefonía.Además, durante el registro, si usted está interesado, Domestic & General le proporcionará información relativa a ofertas de seguro de daños para los electrodomésticos de su hogar. Para conocer cómo Candy Hoover Group S.r.l. y Domestic & General tratan sus datos visite <0>www.candy-home.com/es_ES/pages/politica-de-privacidad</0> y <1>www.domesticandgeneral.com/candy</1>.',
  ],
  phoneNumberAsterixLinks: [
    'https://www.candy-home.com/es_ES/pages/politica-de-privacidad',
    'https://www.domesticandgeneral.com/candy',
  ],
  languageSwitcherLabel: 'País',
  languageSwitcherPlaceholder: 'Seleccione su país',
  languageList: [
    { label: 'Alemania', value: 'de-DE' },
    { label: 'Reino Unido', value: 'en-EN' },
    { label: 'España', value: 'es-ES' },
    { label: 'Francia', value: 'fr-FR' },
    { label: 'Italia', value: 'it-IT' },
  ],
  cookiePolicy: {
    title: 'Política de cookies',
    sections: [
      {
        description: [
          'Las cookies son archivos de texto que contienen pequeñas cantidades de información y que se descargan en su ordenador personal, su móvil o cualquier otro dispositivo cuando visita un sitio web para recordar información acerca de usted, como, por ejemplo, la preferencia de idioma o su información para iniciar sesión. Las cookies, además, son necesarias para el funcionamiento adecuado del sitio, ya que identifican qué páginas ha visitado y las acciones que ha realizado antes. Por ejemplo, nos aportan información importante relativa al número de visitantes o las páginas que visitan cuando están en nuestro sitio. También son necesarias para identificar posibles problemas y áreas de mejora. Esto nos ayuda a adaptar el sitio y mejorar su calidad. Estas cookies son establecidas por nosotros, y se llaman cookies propias. También utilizamos cookies de terceros, que provienen de un dominio diferente al de la página que usted visita. Utilizamos estas cookies para diversas funciones, como, por ejemplo, protegerle contra cualquier bot malicioso.. Específicamente, usamos las cookies y otras tecnologías de seguimiento para los siguientes propósitos:',
        ],
        heading: '¿Qué son las cookies?',
      },
      {
        heading: 'Gestión de cookies',
        description: [
          'Para modificar su configuración de cookies, utilice nuestra herramienta de gestión de cookies o también puede hacerlo a través del icono de seguridad que encontrará en cada página. Puede bloquear el uso de algunas o todas las cookies y tecnologías similares que utilizamos en el sitio web haciendo clic en las diferentes categorías de cookies. Sin embargo, tenga en cuenta que esto puede afectar su experiencia en el sitio web y los servicios que ofrecemos. También debe tener en cuenta que al borrar todas las cookies de su navegador se eliminarán las que almacenan sus preferencias.',
          'La mayoría de los navegadores le indicarán cómo dejar de aceptar nuevas cookies, cómo hacer que el navegador le notifique cuando recibe una nueva cookie y cómo desactivar las cookies por completo. Para borrar las cookies existentes en su navegador, puede seleccionar la opción de borrar el historial de navegación y optar por eliminar o borrar las cookies.',
          'Algunos navegadores, como Chrome y Firefox, le permiten cambiar su configuración para navegar en modo "incógnito", limitando la cantidad de datos colocados en su máquina y eliminando automáticamente cualquier cookie persistente colocada en su dispositivo cuando finaliza su sesión de navegación.',
          'Alternativamente, puede visitar <0>www.aboutcookies.org</0>, que contiene información completa sobre cómo eliminar las cookies en una amplia variedad de navegadores.',
          'Si desea obtener más información sobre las cookies en general, cómo comprobar qué terceros las tienen habilitadas en su dispositivo móvil y cómo desactivar algunas de ellas, visite <1>www.networkadvertising.org/mobile-choices</1>. En su dispositivo móvil, también puede seleccionar «Limitar el seguimiento de anuncios» (en dispositivos iOS) o «Desactivar anuncios basados en intereses» (en Android), lo que le permite limitar el uso de información sobre su uso de aplicaciones para mostrar anuncios adaptados a sus intereses.',
        ],
        links: [
          'https://www.aboutcookies.org',
          'https://www.networkadvertising.org/mobile-choices',
        ],
      },
      {
        heading: 'Sitios web de terceros',
        description: [
          'Este sitio web puede contener enlaces o estar enlazado a otros sitios web. Al hacer clic en el sitio web enlazado, abandonará este sitio web y el contenido que controlamos y será enlazado al sitio web de ese tercero. No tenemos control sobre el contenido del sitio web enlazado y no nos hacemos responsables de la información ni del funcionamiento del mismo. Sin embargo, eliminaremos el enlace al sitio web enlazado si tenemos conocimiento de que se proporciona contenido ilegal o de otro tipo en el mismo. Los operadores del sitio web enlazado pueden recopilar información sobre usted que utilizarán de acuerdo con su política de privacidad, que puede ser diferente de la nuestra. No controlamos la configuración de las cookies en el sitio web enlazado, por lo que le sugerimos que consulte los sitios web enlazados para obtener más información sobre sus cookies y cómo gestionarlas. ',
        ],
      },
      {
        heading: 'Seguridad ',
        description: [
          'Nos comprometemos con la seguridad de este sitio web y con ofrecer una interfaz de usuario libre de fraudes. Toda la información intercambiada se cifra mediante software SSL y hemos hecho todo lo posible para garantizar que su información personal esté protegida contra el acceso no autorizado.',
        ],
      },
    ],
  },
  termsAndConditions: {
    title: 'Términos y condiciones del sitio web',
    content: [
      {
        heading: 'Aviso Legal',
        paragraphs: [
          'Bienvenido a nuestro sitio web. Le informamos de que esta página web es titularidad de Domestic & General Group Limited y es gestionada por Domestic & General Insurance Europe AG, Sucursal en España, con CIF W2765940H, inscrita en el Registro Administrativo de Entidades Aseguradoras de la Dirección General de Seguros y Fondos de Pensiones, bajo la clave E0240 y en el Registro Mercantil de Madrid al tomo 38974, libro 0, folio 13, hoja M692570.',
          'Domestic and General Insurance Europe AG está autorizada por la Autoridad Federal de Supervisión Financiera Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin), con domicilio en Graurheindorfer Str. 108, 53117 Bonn',
          'Puede ponerse en contacto con nosotros a través de los siguientes medios:',
          '- Correo electrónico <0>soporte.clientes@domesticandgeneral.com</0>',
          '- Número de teléfono 900 827 437',
        ],
        links: ['mailto:soporte.clientes@domesticandgeneral.com'],
      },
      {
        heading: 'Términos y condiciones de uso',
        paragraphs: [
          'Al acceder a nuestro sitio Web o hacer uso de él, usted acepta los siguientes términos y condiciones, junto con cualquier otro aviso legal que se encuentre en el sitio web (los “Términos de uso y condiciones”). Le rogamos que los lea detenidamente antes de navegar por el sitio. Solo podrá utilizar este sitio web conforme a estos Términos de uso y condiciones. Si no acepta estos Términos y condiciones de uso y no tiene intención de regirse por ellos, no debe utilizar este sitio web y debe abandonarlo inmediatamente.',
        ],
      },
      {
        heading: 'Contenido',
        paragraphs: [
          'Domestic & General podrá modificar estos Términos de uso y condiciones en cualquier momento mediante actualizaciones en esta misma página. Usted quedará vinculado por dichas modificaciones. Si bien hemos tomado precauciones razonables para verificar la exactitud y vigencia de los materiales contenidos en este sitio web, no ofrecemos ninguna garantía expresa o implícita ni representación sobre los contenidos de éste, incluidas las garantías implícitas de idoneidad para un fin determinado.',
        ],
      },
      {
        heading: 'Definiciones',
        paragraphs: [
          'En esta página: “Domestic & General”, “nosotros” o “nuestro” se refiere a Domestic & General Group Limited, Domestic & General Insurance Europe AG, sucursal en España y cualquier otra empresa del grupo Domestic & General.',
          'En esta página: “usted” se refiere al usuario de este sitio web.',
          'En esta página: "Sitio Web" hace referencia al sitio de registro que es propiedad de Domestic & General y que está administrado en su nombre y representación.',
        ],
      },
      {
        heading: 'Descargo de responsabilidad',
        paragraphs: [
          'El contenido de este sitio web se proporciona únicamente con fines informativos y sin garantías de ningún tipo, incluidas las relativas a su exactitud o integridad. Domestic & General no se hace responsable de los daños derivados del uso del sitio web. No debe basarse exclusivamente en el contenido de este sitio web; consulte siempre la documentación de su plan o contrato. Estos Términos de uso no modifican ningún otro contrato entre usted y Domestic & General.',
        ],
      },

      {
        heading: 'Contenido de terceros',
        paragraphs: [
          'Este sitio web puede contener enlaces a sitios web de terceros que no son gestionados por Domestic & General. No nos responsabilizamos del contenido de dichos sitios ni de su disponibilidad, ya que no están supervisados por Domestic & General.',
        ],
      },
      {
        heading: 'Uso de los materiales',
        paragraphs: [
          'Salvo autorización expresa por parte de Domestic & General, ningún contenido de este sitio web podrá ser copiado, reproducido, republicado, cargado, transmitido o distribuido de ninguna forma. Puede descargar materiales únicamente para uso personal, siempre que conserve todos los avisos de derechos de autor y otros avisos de propiedad contenidos en los materiales. No está permitido distribuir, modificar, transmitir, reutilizar, volver a publicar o utilizar el contenido de nuestro sitio web para fines públicos o comerciales sin el consentimiento previo por escrito de Domestic & General. Tenga en cuenta que todos los derechos de autor, así como otros derechos de propiedad intelectual sobre cualquier material contenido en este Sitio Web son propiedad de Domestic & General. El Sitio Web contiene marcas comerciales, incluidos el nombre y el logotipo de Domestic & General, que pertenecen a Domestic & General. Ningún logotipo o imagen que se encuentre en el Sitio Web se puede retransmitir o copiar, total o parcialmente, salvo que esté autorizado expresamente por el propietario. Domestic & General no garantiza que el uso que usted haga del contenido de nuestro sitio web no infrinja los derechos de terceros.',
        ],
      },

      {
        heading: 'Legislación Aplicable',
        paragraphs: [
          'Domestic & General no garantiza que los materiales de nuestro sitio web sean apropiados o estén disponibles para su uso en cualquier ubicación. Aquellos que eligen acceder a nuestro sitio web lo hacen por su propia iniciativa y son responsables del cumplimiento de las leyes locales. El contenido de este sitio web se regirá e interpretará conforme a la legislación española.',
        ],
      },

      {
        heading: 'Propiedad de la Información',
        paragraphs: [
          'Cualquier información (que no sea información personal) que usted transmita a Domestic & General a través de nuestro sitio web, por correo electrónico o por cualquier otro medio, será tratada como no confidencial. La información personal se manejará de acuerdo con nuestro <0>Aviso de privacidad</0>.',
        ],
        links: ['https://www.domesticandgeneral.com/candy'],
      },
    ],
  },
};
